<template>
  <v-container fluid class="product-analytics-container pa-0">
    <!-- Empty State -->
    <v-flex
      v-if="productList.length <= 0"
      fluid
      class="whiteBackGround customBorderRadius"
    >
      <first-page
        :img-src="imgSrc"
        :dialog-name.sync="dialogProductAdd"
        :points="points"
        :headline="headline"
      ></first-page>
    </v-flex>

    <!-- Main Content -->
    <div v-if="productList.length > 0">
      <!-- Modern Header -->
      <div v-if="showHeader" class="header-section mb-6">
        <v-btn icon small class="header-close-btn" @click="dismissHeader">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
        <div class="d-flex align-center mb-4">
          <div class="header-icon-wrapper mr-4">
            <v-icon size="32" color="#FFFFFF">mdi-chart-box-outline</v-icon>
          </div>
          <div class="flex-grow-1">
            <h1 class="page-title mb-1">Product Analytics</h1>
            <p class="page-subtitle mb-0">
              Track products, revenue, and customer engagement
            </p>
          </div>
          <v-spacer></v-spacer>
          <div class="d-none d-sm-flex">
            <v-chip
              class="mr-2 white--text"
              color="white"
              small
              style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
            >
              <v-icon left x-small color="white">mdi-package-variant</v-icon>
              Products
            </v-chip>
            <v-chip
              class="mr-2 white--text"
              color="white"
              small
              style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
            >
              <v-icon left x-small color="white">mdi-chart-line</v-icon>
              Analytics
            </v-chip>
            <v-chip
              class="white--text"
              color="white"
              small
              style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
            >
              <v-icon left x-small color="white">mdi-currency-inr</v-icon>
              Revenue
            </v-chip>
          </div>
        </div>
      </div>

      <v-row class="ma-0 px-3">
        <v-col cols="12" md="5">
          <v-card class="modern-card customBorderRadius" elevation="0">
            <div class="card-header">
              <div class="d-flex align-center">
                <div class="icon-badge mr-3">
                  <v-icon color="#FFFFFF">mdi-format-list-bulleted</v-icon>
                </div>
                <div>
                  <h3 class="card-title mb-0">Products</h3>
                  <p class="card-subtitle mb-0">Manage your product catalog</p>
                </div>
              </div>
            </div>
            <v-divider></v-divider>
            <v-card-text class="pa-3">
              <!-- Search Bar -->
              <div class="d-flex mb-3">
                <v-text-field
                  v-model="search"
                  dense
                  outlined
                  hide-details
                  placeholder="Search products..."
                  prepend-inner-icon="mdi-magnify"
                  style="border-radius: 8px;"
                  class="flex-grow-1 mr-2"
                />
                <v-btn
                  color="primary"
                  depressed
                  style="border-radius: 8px;"
                  @click="getProducts"
                >
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </div>

              <!-- Product List -->
              <v-list two-line class="product-list">
                <v-list-item-group
                  v-model="selectedIndex"
                  mandatory
                  color="primary"
                >
                  <v-list-item
                    v-for="product in productList"
                    :key="product.id"
                    class="product-list-item"
                    @click="selectedProductItem(product)"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-icon :color="product.hex" size="12">mdi-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ product.title }}
                      </v-list-item-title>
                      <v-list-item-subtitle
                        v-if="product.sku"
                        class="text-caption"
                      >
                        SKU: {{ product.sku }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle
                        v-if="product.price"
                        class="text-caption"
                      >
                        ₹{{ product.price }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            v-bind="attrs"
                            v-on="on"
                            @click.stop
                          >
                            <v-icon size="20">mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="productEdit(product)">
                            <v-list-item-icon class="mr-2">
                              <v-icon size="18">mdi-pencil</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>Edit</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="del(product)">
                            <v-list-item-icon class="mr-2">
                              <v-icon size="18" color="error"
                                >mdi-delete</v-icon
                              >
                            </v-list-item-icon>
                            <v-list-item-title>Delete</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-list-item-action>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-card-text>
          </v-card>

          <!-- Add Product Button -->
          <v-btn
            block
            color="primary"
            depressed
            class="mt-3"
            style="border-radius: 8px;"
            @click="openDialog"
          >
            <v-icon left>mdi-plus-circle</v-icon>
            Add Product
          </v-btn>
        </v-col>
        <v-col cols="12" md="7">
          <!-- Stats Cards -->
          <v-row v-if="totalCount > 0" class="ma-0 mb-3">
            <v-col cols="12" md="4" class="pa-1">
              <v-card class="stat-card customBorderRadius" elevation="0">
                <div class="stat-card-accent stat-accent-blue"></div>
                <v-card-text class="text-center py-8">
                  <v-icon size="40" color="#2196F3" class="mb-2"
                    >mdi-account-group</v-icon
                  >
                  <h2 class="stat-value mb-1">{{ totalCount }}</h2>
                  <p class="stat-label mb-0">Total Contacts</p>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4" class="pa-1">
              <v-card class="stat-card customBorderRadius" elevation="0">
                <div class="stat-card-accent stat-accent-green"></div>
                <v-card-text class="text-center py-8">
                  <v-icon size="40" color="#37cf37" class="mb-2"
                    >mdi-currency-inr</v-icon
                  >
                  <h2 class="stat-value mb-1">₹{{ totalRevenue }}</h2>
                  <p class="stat-label mb-0">Total Revenue</p>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4" class="pa-1">
              <v-card class="stat-card customBorderRadius" elevation="0">
                <div class="stat-card-accent stat-accent-purple"></div>
                <v-card-text class="py-4">
                  <pie-chart
                    style="height: 150px; width: 100%;"
                    :chart-data="datacollection"
                    :options="{
                      responsive: true,
                      legend: {
                        position: 'bottom',
                        labels: { fontColor: 'black' },
                        display: false,
                      },
                      maintainAspectRatio: false,
                    }"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Contact Details Table -->
          <v-card class="modern-card customBorderRadius" elevation="0">
            <div class="card-header">
              <div class="d-flex align-center">
                <div class="icon-badge mr-3">
                  <v-icon color="#FFFFFF">mdi-account-details</v-icon>
                </div>
                <div>
                  <h3 class="card-title mb-0">Contact Details</h3>
                  <p class="card-subtitle mb-0">Customer engagement insights</p>
                </div>
              </div>
            </div>
            <v-divider></v-divider>
            <v-data-table
              :headers="contactHeaders"
              :items="contactDetails"
              :items-per-page="records"
              hide-default-footer
              :loading="loading"
              :loading-text="loadingText"
              class="modern-table"
            ></v-data-table>

            <v-card-text class="pagination-section">
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <span class="text-caption mr-2">Rows per page:</span>
                  <v-select
                    v-model="records"
                    :items="recordsPerPage"
                    dense
                    outlined
                    hide-details
                    style="max-width: 80px; border-radius: 8px;"
                    @change="pagination"
                  />
                </div>
                <div class="d-flex align-center">
                  <v-btn
                    icon
                    small
                    :disabled="page == 1"
                    color="primary"
                    @click="prevSet"
                  >
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  <span class="mx-3 text-caption">{{
                    `${page}/${pageCount} of ${totalCount}`
                  }}</span>
                  <v-btn
                    icon
                    small
                    :disabled="page >= pageCount"
                    color="primary"
                    @click="nextSet"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Add/Edit Product Dialog -->
    <v-dialog v-model="dialogProductAdd" max-width="500" scrollable>
      <v-card class="product-dialog customBorderRadius">
        <div class="dialog-header">
          <div class="d-flex align-center">
            <div class="icon-badge-small mr-3">
              <v-icon color="#FFFFFF" size="20">{{
                editedIndex === -1 ? "mdi-plus-box" : "mdi-pencil"
              }}</v-icon>
            </div>
            <div>
              <h3 class="dialog-title mb-0">{{ formTitle }} Product</h3>
            </div>
          </div>
        </div>
        <v-divider></v-divider>

        <v-card-text class="pt-4 dialog-content">
          <v-text-field
            v-model="product.title"
            label="Product Name"
            outlined
            dense
            hide-details="auto"
            class="mb-4 customBorderRadius"
            :rules="rulesProductname"
            :value="product.title.toUpperCase()"
            @input="product.title = $event.toUpperCase()"
          />

          <v-textarea
            v-model="product.desc"
            label="Description"
            placeholder="Optional description"
            outlined
            dense
            hide-details="auto"
            class="mb-4 customBorderRadius"
            rows="3"
          />

          <v-text-field
            v-model="product.sku"
            label="SKU"
            placeholder="Enter SKU"
            outlined
            dense
            hide-details="auto"
            class="mb-4 customBorderRadius"
          />

          <v-text-field
            v-model="product.price"
            label="Price"
            placeholder="Enter price"
            outlined
            dense
            hide-details="auto"
            class="mb-4 customBorderRadius"
            type="number"
            prefix="₹"
          />

          <div class="mb-2">
            <label class="text-caption text--secondary mb-2 d-block"
              >Product Color</label
            >
            <div class="color-picker-wrapper">
              <v-color-picker
                v-model="product.hex"
                hide-mode-switch
                mode="hexa"
                class="mx-auto"
                style="max-width: 100%;"
              />
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            style="border-radius: 8px;"
            @click="dialogProductAdd = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            depressed
            style="border-radius: 8px;"
            @click="addProduct"
          >
            {{ editedIndex === -1 ? "Add" : "Save" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
const _ = require("lodash");
import fb from "../js/firebase";
import validator from "../js/validator.js";
import { mapGetters } from "vuex";
import moment from "moment-timezone";
//import Breadcrumbs from "../components/breadcrumbs.vue";
import PieChart from "../js/pie.js";
import firstPage from "@/components/firstPage.vue";

export default {
  components: {
    PieChart,
    firstPage,
  },
  data() {
    return {
      showHeader: false,
      headline: "Manage Inventory & Boost Sales",
      points: [
        "Effortless product management: Add, update & manage products/services with ease.",
        "Smart tagging for telecallers: Tag customer interest in specific products during calls.",
        "Data-driven sales insights: Track revenue per product, identify buying trends.",
      ],
      recordsPerPage: [5, 20, 50, 100, 250, 500],
      records: 5,
      page: 1,
      pageCount: 0,
      loading: false,
      loadingText: "Loading..Please Wait",
      options: {},
      datacollection: [],
      totalCount: "",
      dialogProductAdd: false,
      productItem: {
        title: "",
        desc: "",
        hex: "",
        sku: "",
        price: "",
      },
      preloader: false,
      product: {
        title: "",
        desc: "",
        hex: "",
        sku: "",
        price: "",
      },
      selectedProduct: "",
      rulesProductname: [
        (v) =>
          /^\w+$/.test(v) ||
          "Maximum Fifteen characters & Must Not Contain Special Characters Or Space",
      ],
      productList: [],
      items: [],
      contactHeaders: [
        {
          text: "Name",
          align: "left",
          sortable: false,
          value: "name",
        },
        {
          text: "Phone",
          align: "left",
          sortable: false,
          value: "phone",
        },
        {
          text: "Disposition",
          align: "left",
          sortable: false,
          value: "dispo",
        },
        {
          text: "calledOn",
          align: "left",
          sortable: false,
          value: "formatedTime",
        },
      ],
      contactDetails: [],
      editedIndex: -1,
      totalRevenue: 0,
      search: "",
      selectedIndex: 0,
    };
  },
  computed: {
    ...mapGetters([
      "ENDPOINT",
      "user",
      "dispoList",
      "fieldList",
      "teams",
      "selectedTeam",
      "selectedList",
    ]),
    formTitle() {
      return this.editedIndex === -1 ? "New" : "Edit";
    },
    paginate() {
      return this.$store.getters.paginate;
    },
    imgSrc() {
      return require("../assets/products.svg");
    },
  },
  watch: {
    // productList() {
    //   this.preloader = false;
    // },
  },
  async created() {
    this.showHeader = localStorage.getItem("productHeaderDismissed") !== "true";
    await this.getProducts();
    this.selectedProductItem(this.productList[0]);
  },

  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("productHeaderDismissed", "true");
    },
    isMobile() {
      if (window.innerWidth <= 760) {
        return true;
      } else {
        return false;
      }
    },
    /** PAGINATION */
    nextSet() {
      this.page++;
      this.pagination();
    },
    prevSet() {
      this.page--;
      this.pagination();
    },
    async pagination() {
      this.pageCount = Math.ceil(this.totalCount / this.records);
      if (this.page > this.pageCount) {
        this.page = 1; //set default
      }

      this.contactDetails = await this.fetchContactLogs(
        this.records,
        this.records * this.page - this.records,
        this.selectedProduct,
      );

      // Save default pagination of seperate pages
      this.paginate[
        this.$router.currentRoute.name
          .toString()
          .trim()
          .replace(/ /g, "")
          .toLowerCase()
      ] = {
        page: this.page,
        pageCount: this.pageCount,
        records: this.records,
        totalCount: this.totalCount,
      };
      this.$store.commit("setPagination", this.paginate);
      return;
    },
    async totalContactCount(product) {
      var postObj = {
        productsId: [product.id],
        type: "count",
      };

      await this.$http
        .post(`${this.ENDPOINT}/products/get-contact-by-productIds`, postObj)
        .then((response) => {
          this.totalCount = response.body.count;
          this.pageCount = Math.ceil(this.totalCount / this.records);

          this.totalRevenue = product.price * this.totalCount;
        });
    },

    async totalGraphCount(product) {
      var self = this;
      var postObj = {
        productsId: [product.id],
        type: "graph",
      };

      this.$http
        .post(`${this.ENDPOINT}/products/get-contact-by-productIds`, postObj)
        .then((contacts) => {
          contacts = contacts.body;
          console.log("Contacts: ", contacts);
          var called = 0,
            uncalled = 0;
          _.each(contacts, (c) => {
            if (c._id == "NEW") uncalled += c.count;
            else called += c.count;
          });
          self.called = called;
          self.uncalled = uncalled;
          // console.log("Called: ",self.called);
          // console.log("Uncalled: ",self.uncalled);
          var colors = [];
          var labels = [];
          contacts = _.orderBy(contacts, ["count"], ["desc"]);
          if (contacts.length > 10) {
            var contactNew = contacts.slice(0, 10);
            var contactOther = {
              _id: "Others",
              count: 0,
            };
            for (let i = 10; i < contacts.length; i++) {
              contactOther.count += contacts[i].count;
            }
            contacts = contactNew.concat(contactOther);
          }

          console.log("contacts", contacts);

          _.each(contacts, (val) => {
            val = _.find(self.dispoList, (d) => {
              return d.title == val._id;
            });
            if (val == undefined) {
              colors.push("#31a848");
              labels.push("NEW");
            } else {
              colors.push(val.hex);
              labels.push(val.title);
            }
          });
          self.labelShown = false;
          // var labels = _.map(contacts, "_id");
          // Populate Chart
          self.datacollection = {
            datasets: [
              {
                backgroundColor: colors,
                data: _.map(contacts, "count"),
              },
            ],
            labels: labels,
          };
        });
    },

    async fetchContactLogs(limit, skip, product) {
      try {
        this.loading = true;
        this.loadingText = "Loading... Please wait";

        if (limit == undefined || limit == undefined) {
          limit = 5;
          skip = 0;
        }

        var response = await this.$http.post(
          `${this.ENDPOINT}/products/get-contact-by-productIds`,
          {
            limit: limit,
            skip: skip,
            productsId: [product.id],
            type: "data",
          },
        );

        response = _.without(
          _.map(response.body, (r) => {
            if (r.calledOn != undefined) {
              r.formatedTime = moment(r.calledOn).format("YYYY-MM-DD hh:mm A");
            }
            return r;
          }),
          undefined,
        );
        response = _.orderBy(response, "calledOn", "desc");

        this.loading = false;
        this.loadingText = "";
        return response;
      } catch (e) {
        this.loading = false;
        this.loadingText = "Something went wrong while fetching call logs";
      }
    },

    async selectedProductItem(product) {
      this.contactDetails = [];

      await this.totalContactCount(product);

      await this.totalGraphCount(product);

      this.contactDetails = await this.fetchContactLogs(
        this.records,
        this.records * this.page - this.records,
        product,
      );
      this.selectedProduct = product;
    },
    openDialog() {
      this.editedIndex = -1;
      this.product = {
        title: "",
        desc: "",
        hex: "",
        sku: "",
        price: "",
      };
      this.dialogProductAdd = true;
    },
    uppercase() {
      this.product.title = this.product.title.toUpperCase();
    },
    productEdit(item) {
      console.log(item);
      this.editedIndex = this.productList.indexOf(item);
      this.product = Object.assign(item);
      this.dialogProductAdd = true;
    },
    updateProductName() {
      const self = this;

      if (this.productEditedItem.title == "") {
        return self.$swal({
          type: "error",
          text: "Please Enter Product Name",
        });
      }

      self.$http
        .patch(
          `${this.$store.state.ENDPOINT}/products/${self.productEditedItem.id}`,
          self.productEditedItem,
        )
        .then(
          (response) => {
            self.dialogProductEdit = false;
            self.getProducts();
            self.$swal({
              type: "success",
              text: "Product Updated Successfully",
            });
          },
          (error) => {
            self.$swal({
              type: "warning",
              text: "Error occured. Please try again.",
            });
          },
        );
    },

    async getProducts() {
      const self = this;
      var filter = {
        where: {
          companyId: this.$store.getters.user.companyId,
        },
      };
      if (this.search != "") {
        filter.where.title = { like: `${this.search}*`, options: "i" };
      }

      try {
        var response = await this.$http.get(
          `${self.$store.state.ENDPOINT}/products?filter=${encodeURIComponent(
            JSON.stringify(filter),
          )}`,
        );
        self.productList = response.body;

        self.loader = false;
      } catch (err) {
        console.log("Error", err);

        self.loader = false;

        self.$swal({
          type: "error",
          text: "Something went wrong. Please try again",
        });
      }
    },

    addProduct() {
      var self = this;
      if (this.product.title == "") {
        return self.$swal({
          type: "error",
          text: "Please Enter Product Name",
        });
      }

      console.log(
        "this.product.title",
        this.product.title.toUpperCase().trim(),
      );

      this.product.title = this.product.title.toUpperCase().trim();
      this.preloader = true;

      if (this.editedIndex > -1) {
        var jsonObj = {
          title: this.product.title,
          hex: this.product.hex,
          desc: this.product.desc,
          sku: this.product.sku,
          price: this.product.price,
          companyId: this.$store.getters.user.companyId,
        };

        self.$http
          .patch(
            `${this.$store.state.ENDPOINT}/products/${self.product.id}`,
            jsonObj,
          )
          .then(
            (response) => {
              this.dialogProductAdd = false;
              self.getProducts();
              self.$swal({
                type: "success",
                text: "Product Updated Successfully",
              });
            },
            (error) => {
              self.$swal({
                type: "warning",
                text: "Error occured. Please try again.",
              });
            },
          );
      } else {
        this.product.companyId = this.$store.getters.user.companyId;

        this.$http
          .post(`${this.$store.state.ENDPOINT}/products`, this.product)
          .then((response) => {
            this.product = {};
            this.product.title = "";
            this.product.hex = "";
            this.product.desc = "";
            this.product.sku = "";
            this.product.price = "";
            this.preloader = false;
            this.dialogProductAdd = false;
            this.getProducts();

            self.$swal({
              type: "success",
              text: "Product Added Successfully",
            });

            fb.log("create_products");
          })
          .catch((e) => {
            this.preloader = false;
            this.$swal({
              type: "error",
              text: "Sorry, same product is already present",
            });
          });
      }
    },
    del(item) {
      this.preloader = true;

      this.$swal
        .fire({
          title: "Are you sure?",
          text: "Delete this product?",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "cancel",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value) {
            this.$http
              .delete(`${this.$store.state.ENDPOINT}/products/${item.id}`)
              .then((response) => {
                this.preloader = false;
                this.getProducts();
                // this.$store.dispatch("getProducts", {
                //   companyId: this.$store.getters.user.companyId,
                // });
                fb.log("delete_product");
              })
              .catch((e) => {
                this.preloader = false;
                console.log(e);
              });
          }
          this.preloader = false;
        });
    },
  },
};
</script>

<style scoped>
.product-analytics-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .product-analytics-container {
  background-color: #121212 !important;
}

.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px;
  padding: 32px;
  color: white;
  box-shadow: 0 4px 20px rgba(55, 207, 55, 0.3);
}

.header-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: white;
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.modern-card {
  background: var(--v-background-base, white);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.modern-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.customBorderRadius {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.card-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 12px 12px 0 0;
}

.icon-badge {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

.card-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
}

.product-dialog {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.dialog-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
}

.icon-badge-small {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

.dialog-content {
  max-height: 60vh;
  overflow-y: auto;
}

/* Custom scrollbar for dialog */
.dialog-content::-webkit-scrollbar {
  width: 8px;
}

.dialog-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 0 12px 12px 0;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #37cf37;
  border-radius: 4px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #2ba82b;
}

/* Rounded inputs in dialog */
.customBorderRadius .v-text-field__slot,
.customBorderRadius fieldset {
  border-radius: 12px !important;
}

.customBorderRadius .v-input__slot {
  border-radius: 12px !important;
  overflow: hidden;
}

.customBorderRadius.v-text-field--outlined fieldset {
  border-radius: 12px !important;
}

.customBorderRadius.v-textarea--outlined fieldset {
  border-radius: 12px !important;
}

.color-picker-wrapper {
  overflow: hidden;
  border-radius: 8px;
}

.color-picker-wrapper .v-color-picker {
  border-radius: 8px !important;
}

.product-list {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
}

.product-list-item {
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
}

.product-list-item:hover {
  background: rgba(55, 207, 55, 0.05);
}

.stat-card {
  background: white;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.stat-card-accent {
  height: 4px;
  width: 100%;
}

.stat-accent-blue {
  background: linear-gradient(90deg, #2196f3 0%, #1976d2 100%);
}

.stat-accent-green {
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
}

.stat-accent-purple {
  background: linear-gradient(90deg, #9c27b0 0%, #7b1fa2 100%);
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  line-height: 1;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.modern-table {
  border-radius: 0 !important;
}

.pagination-section {
  background: #fafafa;
  padding: 16px 24px;
}

.whiteBackGround {
  background-color: white;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 960px) {
  .product-list {
    max-height: 50vh;
  }

  .header-icon-wrapper {
    width: 56px;
    height: 56px;
  }

  .page-title {
    font-size: 28px;
  }
}
</style>
